/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { FormGroup, FormControl, Validators } from '@angular/forms';
import { GenericMapper } from './GenericMapper';
import {
  CableTrayFittingSizeDto,
  FittingDto,
  ActionTypeEnumDto,
  CreateCableTraySizeInfoDto,
  CreateCableTrayFittingSizeInfoDto,
  ActionPageEnumDto
} from 'api/models';

export class CableTrayFittingSizeMapper implements GenericMapper {

  createAddSizeObject(dataArray: CableTrayFittingSizeDto[], userID: string, selectedFitting: FittingDto, statusId: ActionTypeEnumDto): any {
    const createSizeList: CreateCableTraySizeInfoDto[] = [];

    let maxID = 0;
    dataArray.forEach(element => {
      element.id = element.id > 0 ? element.id : maxID++;
      const newSize: CreateCableTraySizeInfoDto = {
        sizeID: element.sizeID,
        nominalSize: element.nominalSize,
        bdid: element.bdid,
        weightImperial: element.weightImperial,
        weightMetric: element.weightMetric,
        orderNumber: element.orderNumber !== undefined ? element.orderNumber : '0',
        startPage: element.startPage,
        endPage: element.endPage,
        id: element.id,
        hpid: element.hpid !== undefined ? element.hpid : '0',
        amtechID: element.amtechID > 0 ? element.amtechID : 0,
        sizeUnit: element.pricingUnit !== undefined ? element.pricingUnit : '0',
        discontinued: element.isDiscontinued !== null ? element.isDiscontinued : false
      };

      createSizeList.push(newSize);
    });

    const createfittingSizeInfo: CreateCableTrayFittingSizeInfoDto = {
      fittingEntryID: selectedFitting.fittingEntryID,
      fittingIDNumber: selectedFitting.fittingIDNumber,
      comment: 'New added size',
      bdToolID: selectedFitting.bdToolID,
      changeby: userID,
      actionType: statusId,
      sizeList: createSizeList
    };
    return createfittingSizeInfo;

  }


  mapToFormGroup(source: CableTrayFittingSizeDto, unitId: number, page: ActionPageEnumDto): FormGroup {
    const formGroup = new FormGroup({
      id: new FormControl(source.id),
      isDiscontinued: new FormControl(source.isDiscontinued),
      isPartialySaved: new FormControl(source.isPartialySaved),
      nominalSize: new FormControl({ value: source.nominalSize, disabled: source.isPartialySaved || page === 'Update' },
        Validators.required),
      bdid: new FormControl({ value: source.bdid, disabled: true }, Validators.required),
      hpid: new FormControl({ value: source.hpid, disabled: unitId === 3 || source.isPartialySaved },
        [Validators.required]),
      amtechID: new FormControl({ value: source.amtechID, disabled: unitId === 1 || source.isPartialySaved },
        [Validators.required, Validators.pattern('^[0-9]*$')]),
      // decSize1: new FormControl({ value: source.decSize1, disabled: true }, Validators.required),
      // decSize2: new FormControl({ value: source.decSize2, disabled: true }, Validators.required),
      // decSize3: new FormControl({ value: source.decSize3, disabled: true }, Validators.required),
      // decSize4: new FormControl({ value: source.decSize4, disabled: true }, Validators.required),
      // decSize5: new FormControl({ value: source.decSize5, disabled: true }, Validators.required),
      // decSize6: new FormControl({ value: source.decSize6, disabled: true }, Validators.required),
      orderNumber: new FormControl({ value: source.orderNumber, disabled: source.isPartialySaved }, Validators.required),
      weightImperial: new FormControl({ value: source.weightImperial, disabled: unitId === 3 || source.isPartialySaved },
        [Validators.required, Validators.pattern('^[0-9]+(\.[0-9]?[0-9]?[0-9]?[0-9])?$')]),
      weightMetric: new FormControl({ value: source.weightMetric, disabled: unitId === 1 || source.isPartialySaved },
        [Validators.required, Validators.pattern('^[0-9]+(\.[0-9]?[0-9]?[0-9]?[0-9])?$')]),
      startPage: new FormControl({ value: source.startPage, disabled: source.isPartialySaved }, [Validators.required,
      Validators.pattern('^[0-9]*$')]),
      endPage: new FormControl({ value: source.endPage, disabled: source.isPartialySaved },
        [Validators.required, Validators.pattern('^[0-9]*$')]),
    });
    return formGroup;
  }

  mapToObject(controls): CableTrayFittingSizeDto {
    // tslint:disable-next-line: one-variable-per-declaration
    const newObject = {
      id: controls[`ID`].value,
      isDiscontinued: controls[`Discontinued`].value,
      nominalSize: controls[`NominalSize`].value,
      sizeID: controls[`SizeID`].value,
      bdid: controls[`BDID`].value,
      numberOfConnections: controls[`NumberOfConnections`].value,
      weightImperial: controls[`WeightImperial`].value,
      weightMetric: controls[`WeightMetric`].value,
      orderNumber: controls[`OrderNumber`].value,
      startPage: controls[`StartPage`].value,
      endPage: controls[`EndPage`].value,
      hpid: controls[`HPID`].value,
      amtechID: controls[`AmtechID`].value,
      decDepth1: controls[`DecDepth1`].value,
      decDepth2: controls[`DecDepth2`].value,
      decDepth3: controls[`DecDepth3`].value,
      decDepth4: controls[`DecDepth4`].value,
      decDepth5: controls[`DecDepth5`].value,
      decDepth6: controls[`DecDepth5`].value,
      decWidth1: controls[`DecWidth1`].value,
      decWidth2: controls[`DecWidth2`].value,
      decWidth3: controls[`DecWidth3`].value,
      decWidth4: controls[`DecWidth4`].value,
      decWidth5: controls[`DecWidth5`].value,
      decWidth6: controls[`DecWidth6`].value,
    } as CableTrayFittingSizeDto;

    return newObject;
  }

  getTableHeaders(unitid?: number): any {
    let columnHeader;
    if (unitid === 1) {
      columnHeader = {
        bdid: 'BDID',
        nominalSize: 'Nominal Size',
        weightImperial: 'Weight Imperial',
        weightMetric: 'WeightMetric',
        orderNumber: 'Order Number',
        startPage: 'Start Page',
        endPage: 'EndPage',
        hpid: 'HPID',
        decDepth1: 'Dec Depth 1',
        decDepth2: 'Dec Depth 2',
        decDepth3: 'Dec Depth 3',
        decDepth4: 'Dec Depth 4',
        decDepth5: 'Dec Depth 5',
        decDepth6: 'Dec Depth 6',
        decWidth1: 'Dec Width 1',
        decWidth2: 'Dec Width 2',
        decWidth3: 'Dec Width 3',
        decWidth4: 'Dec Width 4',
        decWidth5: 'Dec Width 5',
        decWidth6: 'Dec Width 6',

      };
    } else {
      columnHeader = {
        bdid: 'BDID',
        nominalSize: 'Nominal Size',
        weightImperial: 'Weight Imperial',
        weightMetric: 'WeightMetric',
        orderNumber: 'Order Number',
        startPage: 'Start Page',
        endPage: 'EndPage',
        amtechID: 'AmtechID',
        decDepth1: 'Dec Depth 1',
        decDepth2: 'Dec Depth 2',
        decDepth3: 'Dec Depth 3',
        decDepth4: 'Dec Depth 4',
        decDepth5: 'Dec Depth 5',
        decDepth6: 'Dec Depth 6',
        decWidth1: 'Dec Width 1',
        decWidth2: 'Dec Width 2',
        decWidth3: 'Dec Width 3',
        decWidth4: 'Dec Width 4',
        decWidth5: 'Dec Width 5',
        decWidth6: 'Dec Width 6',
      };

    }
    return columnHeader;
  }

  setDefaultValues(elementForm: FormGroup, baseObject: CableTrayFittingSizeDto, selectedFitting: FittingDto) {
    elementForm.controls[`NumberOfConnections`].setValue(baseObject.numberOfConnections);
    elementForm.controls[`OrderNumber`].setValue(baseObject.orderNumber);
    elementForm.controls[`DecDepth1`].setValue(baseObject.decDepth1);
    elementForm.controls[`DecDepth2`].setValue(baseObject.decDepth2);
    elementForm.controls[`DecDepth3`].setValue(baseObject.decDepth3);
    elementForm.controls[`DecDepth4`].setValue(baseObject.decDepth4);
    elementForm.controls[`DecDepth5`].setValue(baseObject.decDepth5);
    elementForm.controls[`DecDepth6`].setValue(baseObject.decDepth6);
    elementForm.controls[`WeightImperial`].setValue(baseObject.weightImperial);
    elementForm.controls[`WeightMetric`].setValue(baseObject.weightMetric);
    elementForm.controls[`StartPage`].setValue(baseObject.startPage);
    elementForm.controls[`EndPage`].setValue(baseObject.endPage);
    elementForm.controls[`HPID`].setValue(baseObject.hpid);
    elementForm.controls[`AmtechID`].setValue(baseObject.amtechID);
    elementForm.controls[`DecWidth1`].setValue(baseObject.decWidth1);
    elementForm.controls[`DecWidth2`].setValue(baseObject.decWidth2);
    elementForm.controls[`DecWidth3`].setValue(baseObject.decWidth3);
    elementForm.controls[`DecWidth4`].setValue(baseObject.decWidth4);
    elementForm.controls[`DecWidth5`].setValue(baseObject.decWidth5);
    elementForm.controls[`DecWidth6`].setValue(baseObject.decWidth6);
    elementForm.controls[`BDID`].setValue((String)(selectedFitting.fittingIDNumber + baseObject.sizeID));
  }

  setValuesByElement(elementForm: FormGroup, baseObject: CableTrayFittingSizeDto, nominalSizeList: any, isPartialySaved?: boolean) {
    const selectedNominalSize = nominalSizeList.find(x => x.NominalSize === baseObject.nominalSize);
    elementForm.controls[`ID`].setValue(baseObject.id);
    elementForm.controls[`Discontinued`].setValue(baseObject.isDiscontinued);
    elementForm.controls[`NominalSize`].setValue(selectedNominalSize);
    elementForm.controls[`OrderNumber`].setValue(baseObject.orderNumber);
    elementForm.controls[`NumberOfConnections`].setValue(baseObject.numberOfConnections);
    elementForm.controls[`WeightImperial`].setValue(baseObject.weightImperial);
    elementForm.controls[`WeightMetric`].setValue(baseObject.weightMetric);
    elementForm.controls[`StartPage`].setValue(baseObject.startPage);
    elementForm.controls[`EndPage`].setValue(baseObject.endPage);
    elementForm.controls[`HPID`].setValue(baseObject.hpid);
    elementForm.controls[`AmtechID`].setValue(baseObject.amtechID);
    elementForm.controls[`DecDepth1`].setValue(baseObject.decDepth1);
    elementForm.controls[`DecDepth2`].setValue(baseObject.decDepth2);
    elementForm.controls[`DecDepth3`].setValue(baseObject.decDepth3);
    elementForm.controls[`DecDepth4`].setValue(baseObject.decDepth4);
    elementForm.controls[`DecDepth5`].setValue(baseObject.decDepth5);
    elementForm.controls[`DecDepth6`].setValue(baseObject.decDepth6);
    elementForm.controls[`DecWidth1`].setValue(baseObject.decWidth1);
    elementForm.controls[`DecWidth2`].setValue(baseObject.decWidth2);
    elementForm.controls[`DecWidth3`].setValue(baseObject.decWidth3);
    elementForm.controls[`DecWidth4`].setValue(baseObject.decWidth4);
    elementForm.controls[`DecWidth5`].setValue(baseObject.decWidth5);
    elementForm.controls[`DecWidth6`].setValue(baseObject.decWidth6);
    elementForm.controls[`BDID`].setValue(baseObject.bdid);
  }

}
